/**
 * Page Actions (Icare) : liste triable/filtrable/recherchable/exportable des
 * actions correctives, avec détection automatique des retards.
 */
import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import useQuickFilter from "@/hooks/useQuickFilter";
import DataTable from "@/components/tables/DataTable";
import SummaryChip from "@/components/tables/SummaryChip";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES, QUICK_FILTER_KPI } from "@/constants/testIds";

const COLUMNS = [
  { key: "id", label: "Numéro" },
  { key: "title", label: "Description de l'action", width: 340, wrap: true },
  { key: "site", label: "Site" },
  { key: "owner_name", label: "Responsable" },
  { key: "due_date", label: "Échéance" },
  { key: "status", label: "Statut" },
  { key: "late", label: "Retard" },
  { key: "source", label: "Origine" },
];

const QUICK_FILTERS = {
  open: (r) => r.status === "Open",
  late: (r) => r.late,
};

export default function ActionsPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);
  const { active: quickFilter, toggle: toggleQuickFilter, clear: clearQuickFilter, apply: applyQuickFilter } = useQuickFilter(QUICK_FILTERS);

  useEffect(() => {
    dashboardApi.getActions(filters).then(({ data }) => setData(data));
  }, [filters, refreshKey]);

  const renderCell = (col, row) => {
    if (col.key === "id") {
      return row.link ? (
        <a
          href={row.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`actions-link-${row.id}`}
          className="inline-flex items-center gap-1 font-medium text-[#0B5394] hover:underline"
        >
          {row.id} <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        row.id
      );
    }
    if (col.key === "status") {
      return (
        <Badge variant="outline" className={row.status === "Open" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}>
          {row.status}
        </Badge>
      );
    }
    if (col.key === "late") {
      return row.late ? <Badge className="border-none bg-red-100 text-red-700">En retard</Badge> : <span className="text-slate-400">-</span>;
    }
    return undefined;
  };

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  return (
    <div data-testid={PAGES.actions} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Actions Icare</h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <SummaryChip testId={QUICK_FILTER_KPI.actionsTotal} label="Total" value={data.summary.total} onClick={clearQuickFilter} active={!quickFilter} />
        <SummaryChip testId={QUICK_FILTER_KPI.actionsOpen} label="Ouvertes" value={data.summary.open} tone="blue" onClick={() => toggleQuickFilter("open")} active={quickFilter === "open"} />
        <SummaryChip testId={QUICK_FILTER_KPI.actionsLate} label="En retard" value={data.summary.late} tone="orange" onClick={() => toggleQuickFilter("late")} active={quickFilter === "late"} />
      </div>
      <DataTable testId="actions-table" title="Actions Icare" columns={COLUMNS} data={applyQuickFilter(data.items)} renderCell={renderCell} />
    </div>
  );
}
